import CresAxios from "../../util/CresAxios";

/**
 * Scheduled reports: a saved report, run on a cadence, recorded as evidence.
 *
 * IT RUNS FOR ITS OWNER AND NOBODY ELSE. There is no recipient list in this type because there is
 * none in the table: a schedule produces exactly the rows the person who created it could have
 * produced by running the report themselves. What they do with them afterwards is not this
 * application's problem — and a recipient list is the feature that would make it one.
 *
 * NEITHER THE OWNER NOR THE PERSONA IS SENT. Both are read at the server's request boundary — the
 * owner from the authorized identity, the persona from the visibility headers this app already
 * sends on every call. A body that could name an owner would be a way to make somebody else's
 * account run a report; one that could name a persona would be a way to schedule a scope you do
 * not hold.
 *
 * PHASE ONE DELIVERS NOTHING. The run is recorded and appears in the report's history, exactly as a
 * run you pressed yourself does. Notification and download come later, and deliberately after the
 * persona and ownership rules have been proven by this.
 */

const URL = "t10/v1/report_schedules";

export const FREQUENCIES = ["Hourly", "Daily", "Weekly", "Monthly"] as const;

export type Frequency = typeof FREQUENCIES[number];

/**
 * What the notification carries.
 *
 * "none" IS THE DEFAULT AND WAS THE ONLY BEHAVIOUR. A link can check who is asking, can expire and
 * can be withdrawn; a file in a mailbox is none of those and is readable by anybody that mailbox is
 * forwarded to. Attaching is offered because it was asked for, and it is per schedule and off until
 * chosen, so nothing that already exists starts sending files.
 */
export const ATTACH_FORMATS = [
    {id: "none", label: "A link only"},
    {id: "xlsx", label: "A link and the Excel file"},
    // PARQUET IS FOR SOMETHING THAT READS IT, not for somebody who opens it. Offered because it was
    // asked for; the separate question of whether such a person really wants a DESTINATION rather
    // than a file in a mailbox is untouched by this and still open.
    {id: "parquet", label: "A link and the Parquet file"},
] as const;

export type AttachFormat = typeof ATTACH_FORMATS[number]["id"];

/**
 * What a schedule DELIVERS, which is a different question from what it emails.
 *
 * ATTACHING SENDS A PERSON A FILE; FEEDING PUTS ONE WHERE A PIPELINE READS IT. This is the
 * destination the Parquet request was originally suspected of really being about, and the two are
 * independent: a schedule may notify a person, feed a machine, both, or neither.
 *
 * THE DESTINATION IS NOT NAMED HERE, and cannot be. The server decides where a feed lands from the
 * realm and the schedule; a bucket or a key in this payload would be a way to write a tenant's rows
 * somewhere of the caller's choosing.
 */
export const FEED_FORMATS = [
    {id: "none", label: "Nowhere"},
    {id: "parquet", label: "Parquet, to your feed folder"},
    // BOTH CARRY TYPES TO A MACHINE, and they are not interchangeable: Parquet is columnar and is
    // what a lake wants; Avro is row-oriented and carries its schema INSIDE the file, which is what
    // a streaming consumer and a schema registry want. A pipeline that already speaks one rarely
    // wants the other, so this is a real choice rather than a preference about compression.
    {id: "avro", label: "Avro, to your feed folder"},
] as const;

export type FeedFormat = typeof FEED_FORMATS[number]["id"];

export type ReportSchedule = {
    USID: string;
    ReportUSID: string;
    ReportName?: string | null;
    Frequency: Frequency;
    /** Local time of day, `HH:MM`. Null for an hourly cadence. */
    RunAtTime?: string | null;
    Timezone: string;
    Enabled: boolean;
    NextRunDatetime?: string | null;
    LastRunDatetime?: string | null;
    /** Why the server stopped it: the owner left, or lost the persona. Shown, never hidden. */
    StoppedReason?: string | null;
    /** "none" (a link only) or "xlsx". The server normalises NULL to "none" on the way out. */
    AttachFormat?: AttachFormat;
    /** "none" or "parquet". Independent of AttachFormat -- see FEED_FORMATS. */
    FeedFormat?: FeedFormat;
    /** Where the last delivery landed, so a feed can be seen to be working. Read-only. */
    LastFeedKey?: string | null;
    LastFeedDatetime?: string | null;
    LastFeedRows?: number | null;
};

export function getSchedules(reportUSID?: string): Promise<ReportSchedule[]> {
    return CresAxios({method: "GET", url: URL,
                      ...(reportUSID ? {params: {ReportUSID: reportUSID}} : {})})
        .then((response: any) => response.data?.Schedules ?? []);
}

export function saveSchedule(schedule: {
    USID?: string; ReportUSID: string; Frequency: Frequency;
    RunAtTime?: string | null; Timezone: string; Enabled: boolean;
    AttachFormat?: AttachFormat;
    FeedFormat?: FeedFormat;
}) {
    return CresAxios({method: "PUT", url: URL, data: schedule})
        .then((response: any) => response.data);
}

export function deleteSchedule(usid: string) {
    return CresAxios({method: "DELETE", url: URL, params: {USID: usid}})
        .then((response: any) => response.data);
}

/** The browser's own zone, which is the only sensible default for "every day at 02:00". */
export function localZone(): string {
    try {
        return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
    } catch {
        return "UTC";
    }
}
